// Assignments -> Notifications (owner, 2026-10-07 — docs/decisions.md D-310): who
// receives each notification, per company. It was Rules -> Upload Notifications from
// D-306 until the owner moved it the same day: "Let's rename the Rules > Upload
// Notifications tab to 'Alerts' and move it to Assignments. There, place the
// Alerts tab after Invoices. Since the tab itself is now just called Alerts,
// the table should work for all kinds of alerts. Therefore, the leftmost column
// should be called Alert, and its value for all rows at this point should be
// 'New Upload'." The owner's quotes here say Alerts, the name until D-363.
//
// The owner: "maybe we structure this screen as a table with columns for
// Company, Company Representatives, Controller Representatives, and Other
// Representatives (just in case). We could also add a warning block at the top
// of the page if any company does not have the 1 + 2 required representatives."
// The headers were shorter (AP/AR, Controllers, Others) so they fit; he was open
// to that. ONE ASSIGNED TO COLUMN since 2026-10-07 (owner: "drop the separate
// AP/AR, Controllers, and Others columns in favor of just an Assigned To column.
// However, I still want the New Invoices Upload pop-up and warning to account
// for these three categories"). The table reads Notification · Company · Assigned To;
// the row's editor keeps the three lists and the warning keeps its rule.
//
// THE WARNING'S BUTTON (owner, 2026-10-07), the same kind References → Vendors
// carries: Show These Companies narrows the table to exactly the companies the
// warning names, through the Filters dialog's Recipients dropdown.
//
// THE RULES SCREEN'S CONVENTIONS, where they apply: the shared row density, the
// ROW is the edit control (`rowActivationProps`), one search in the header
// cluster, the yellow `WarningBanner` for the one urgent fact. The editor is the
// Assignments screen's row editor in shape — the same People Field over
// `searchPeople` — because adding a person to a list is the job that dialog
// already does.
//
// A HINT, NEVER THE BOUNDARY: the server re-asks `canManageRulesSection` on
// every route, refuses a person already listed, and stores the address the
// directory gave.
//
// FILTER AND REFRESH (owner, 2026-10-07: "This screen needs Refresh and Filter
// buttons"). Filter is the shared popover, narrowing by Notification and by Company and
// searching companies and people; Refresh re-reads the list. Both sit in the
// header cluster, Filter first, as on Analytics. The row's editor is an
// ordinary dialog, the size of every other Assignments pop-up (owner, the same
// day: "The pop-up at Assignments > Alerts is much bigger than the other
// pop-ups at Assignments. Please normalize it.").
//
// TWO NOTIFICATIONS SINCE D-336 (owner, 2026-10-09): every company has a New Invoices
// Upload row and an Invoice Passed Without Any Approvals row. The new notification's
// editor shows ONE list, headed Assigned To like the table's column, rather
// than the three — "In Assignments, the new alert, Invoice Passed Without Any
// Approvals, if showing fields for AP/AR, Controllers, and Others. There is not
// a specific structure to who should be receiving this one." It needs at least
// one person on that list — "Require at least one person for the new alert,
// though we do not need to recommend roles" — and the warning names a company
// short of it in its own sentence.
//
// SELECT AND BULK-APPLY (owner, 2026-10-08: every such table "includes the
// option to bulk-apply"). The same checkbox column, unlabelled Select All in
// the corner, and Edit N Selected button the other Assignments tabs carry
// (D-341, which merged the red/green Unassign From / Assign to pair into it).
// Its People Field lists everyone on any ticked row — a dashed "2 of 5" tag for
// someone on only some — and a new name is added to every ticked row: to the
// list picked, on a New Invoices Upload row, and to the one list on an Invoice
// Passed Without Any Approvals row. The list is asked for only when someone is
// being added and a New Invoices Upload row is ticked. A ✕ takes the person off
// all of their lists there. There is no bulk endpoint, so each write is the
// single-row route, and one refusal does not abandon the rest.
//
// THE ROW'S EDITOR is one People Field per list (D-341): name tags in a box,
// typing in the box to add, ✕ to take off, everything held until Apply.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Body1,
  Button,
  Caption1,
  Checkbox,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Option,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { ArrowClockwiseRegular } from '@fluentui/react-icons'
import {
  NOTIFICATION_TYPE,
  NOTIFICATION_TYPE_LABEL,
  type NotificationType,
  UPLOAD_NOTIFICATION_KINDS,
  UPLOAD_NOTIFICATION_KIND_LABEL,
  type UploadNotificationKind,
  isSingleListNotification,
} from '@ap/shared'
import { AppDialog } from '../components/AppDialog'
import { DecisionDialog } from '../components/DecisionDialog'
import { type EntityOption } from '../components/EntityPicker'
import { PeopleField, type PersonTag } from '../components/PeopleField'
import { useDialogFormStyles } from '../components/dialogForm'
import { FrozenWhileClosed } from '../components/FrozenWhileClosed'
import { ListFilters } from '../components/ListFilters'
import { ScreenActions } from '../components/ScreenActions'
import { ErrorBanner } from '../components/States'
import { SortableHeaderCell } from '../components/SortableHeaderCell'
import { sortRows, useTableSort } from '../lib/tableSort'
import { WarningBanner } from '../components/WarningBanner'
import { rowActivationProps, useClickableRowClass, useRoomyTableClass } from '../components/tableDensity'
import { useToast } from '../components/useToast'
import { refusedMessage } from '../lib/assignmentRows'
import { NOTHING_CHANGED, countOf, sentenceList } from '../lib/format'
import { errorMessage } from '../api/errors'
import {
  addUploadNotificationRecipient,
  loadCompaniesForManagement,
  loadUploadNotificationRecipients,
  removeUploadNotificationRecipient,
  searchPeople,
} from '../lib/data'
import type { Company, DirectoryUser, UploadNotificationRecipient } from '../lib/types'
import {
  notificationsPhrase,
  assignedTo,
  isShortOfRecipients,
  listOf,
  shortOfRecipientsSentence,
  uploadNotificationRows,
  type UploadNotificationRow,
} from '../lib/uploadNotifications'

const useStyles = makeStyles({
  scroll: { overflowX: 'auto', maxWidth: '100%', minWidth: 0 },
  table: { tableLayout: 'fixed', width: '100%' },
  // The people in a cell, one per line, so a long list reads down the row.
  people: { display: 'flex', flexDirection: 'column', gap: '2px', whiteSpace: 'normal', wordBreak: 'break-word' },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // A cell short of its expected count, in the warning's own colour.
  short: { color: tokens.colorPaletteYellowForeground2 },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  // The bulk strip, as on the other Assignments tabs.
  selBar: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', padding: '8px 0' },
  bulkScope: { display: 'block', color: tokens.colorNeutralForeground3 },
})

const personName = (r: UploadNotificationRecipient) => r.user_name ?? r.user_email

/** The Recipients dropdown's one narrowing value: the rows the warning names. */
const MISSING = 'missing'

const rowKey = (row: UploadNotificationRow) => `${row.notification_type}:${row.company_id}`
/** "Invoice Passed Without Any Approvals for SeniorLIFE York": a row by name, as the
    Assignments rows read ("Manager Approval for SeniorLIFE Altoona"). */
const rowTitle = (row: UploadNotificationRow) => `${NOTIFICATION_TYPE_LABEL[row.notification_type]} for ${row.company_name}`
/** The same name as a field label or the heading over the lists, every word
    capitalized (owner, 2026-10-12 — D-351); the company name exactly as stored.
    Sentences keep `rowTitle`. */
const rowLabel = (row: UploadNotificationRow) => `${NOTIFICATION_TYPE_LABEL[row.notification_type]} For ${row.company_name}`
const sameEmail = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

/** The companies a bulk action reaches, named, for the sentence under its picker. */
const companyNames = (rows: readonly UploadNotificationRow[]) => sentenceList(rows.map((row) => row.company_name))

export function NotificationRecipientsManager() {
  const s = useStyles()
  const roomy = useRoomyTableClass()
  const clickable = useClickableRowClass()
  const toast = useToast()
  const [companies, setCompanies] = useState<Company[] | null>(null)
  const [recipients, setRecipients] = useState<UploadNotificationRecipient[]>([])
  const [error, setError] = useState<string | null>(null)
  const [term, setTerm] = useState('')
  // The Filter popover's two multi-selects; an empty pick does not narrow.
  const [notificationFilter, setNotificationFilter] = useState<string[]>([])
  const [companyFilter, setCompanyFilter] = useState<string[]>([])
  // `MISSING` narrows to the companies the warning names; `''` is any.
  const [missingFilter, setMissingFilter] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  // Rows ticked for a bulk action, by key — the row objects are rebuilt on load.
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set())
  const [bulkOpen, setBulkOpen] = useState(false)
  // SORTED BY NOTIFICATION BY DEFAULT (owner, 2026-10-09: "Alerts should be sorted by
  // Alert"), with the company second; the headers sort as every other
  // Assignments table's do.
  const sort = useTableSort<'notification' | 'company' | 'assigned'>({ key: 'notification', direction: 'asc' })

  const load = useCallback(async () => {
    setError(null)
    try {
      const [cs, rs] = await Promise.all([loadCompaniesForManagement(), loadUploadNotificationRecipients({ active: true })])
      setCompanies(cs)
      setRecipients(rs)
    } catch (e) {
      setError(errorMessage(e))
      setCompanies((prev) => prev ?? [])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const rows = useMemo(() => uploadNotificationRows(companies ?? [], recipients), [companies, recipients])
  const warning = useMemo(() => shortOfRecipientsSentence(rows), [rows])
  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    const shown = rows.filter(
      (row) =>
        (notificationFilter.length === 0 || notificationFilter.includes(row.notification_type)) &&
        (companyFilter.length === 0 || companyFilter.includes(row.company_id)) &&
        (missingFilter === '' || isShortOfRecipients(row)) &&
        (!q ||
          NOTIFICATION_TYPE_LABEL[row.notification_type].toLowerCase().includes(q) ||
          row.company_name.toLowerCase().includes(q) ||
          row.company_id.toLowerCase().includes(q) ||
          row.lists.some((list) =>
            list.recipients.some((r) => `${personName(r)} ${r.user_email}`.toLowerCase().includes(q)),
          )),
    )
    return sortRows(shown, sort.state, {
      notification: (row) => `${NOTIFICATION_TYPE_LABEL[row.notification_type]} ${row.company_name}`,
      company: (row) => `${row.company_name} ${NOTIFICATION_TYPE_LABEL[row.notification_type]}`,
      assigned: (row) => assignedTo(row).map(personName).join(' ') || ' ',
    })
  }, [rows, term, notificationFilter, companyFilter, missingFilter, sort.state])
  /** The warning's button: exactly the companies it names — every notification, no search, no company pick. */
  const showMissing = () => {
    setTerm('')
    setNotificationFilter([])
    setCompanyFilter([])
    setMissingFilter(MISSING)
  }
  const missingShown =
    missingFilter === MISSING && term === '' && notificationFilter.length === 0 && companyFilter.length === 0
  // The dropdowns offer what the table holds, in its own order (by company name).
  const notificationOptions = (Object.values(NOTIFICATION_TYPE) as NotificationType[]).map((t) => ({ value: t, label: NOTIFICATION_TYPE_LABEL[t] }))
  const companyOptions = useMemo(
    () => [...new Map(rows.map((row) => [row.company_id, row.company_name])).entries()].map(([value, label]) => ({ value, label })),
    [rows],
  )
  const openRow = rows.find((row) => rowKey(row) === openId) ?? null
  // SELECT ALL SELECTS WHAT IS ON SCREEN, as on the other Assignments tabs.
  const selectedRows = rows.filter((row) => selected.has(rowKey(row)))
  const allVisibleSelected = visible.length > 0 && visible.every((row) => selected.has(rowKey(row)))
  const toggleAllVisible = () => {
    const keys = visible.map(rowKey)
    setSelected((prev) => (keys.every((k) => prev.has(k)) ? new Set() : new Set(keys)))
  }
  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (!next.delete(key)) next.add(key)
      return next
    })

  if (companies === null) return <Spinner label="Loading notifications…" />

  return (
    <div>
      <ScreenActions>
        <ListFilters
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Notification, company, or person…"
          ariaLabel="Notifications"
          selects={[
            { multiple: true, label: 'Notifications', values: notificationFilter, onChange: setNotificationFilter, options: notificationOptions },
            {
              multiple: true,
              label: 'Companies',
              values: companyFilter,
              onChange: setCompanyFilter,
              options: companyOptions,
            },
            {
              // RECIPIENTS, FEWER THAN REQUIRED (owner, 2026-10-09: "Required
              // Assignments at Assignments does not really make any sense to
              // me"): the rows short of the floor the warning names.
              label: 'Recipients',
              value: missingFilter,
              onChange: (v) => setMissingFilter(v === MISSING ? MISSING : ''),
              options: [{ value: MISSING, label: 'Fewer Than Required' }],
            },
          ]}
        />
        <Tooltip content="Refresh" relationship="label">
          <Button appearance="secondary" icon={<ArrowClockwiseRegular />} onClick={() => void load()} />
        </Tooltip>
      </ScreenActions>
      <ErrorBanner message={error} />
      {/* THE ONE URGENT FACT, SAID ONCE (owner, 2026-10-07), naming each
          company exactly. Counted over every active company, not the search. */}
      {warning !== '' && (
        <WarningBanner
          action={
            missingShown ? undefined : (
              <Button appearance="secondary" onClick={showMissing}>
                Show These Companies
              </Button>
            )
          }
        >
          {warning}
        </WarningBanner>
      )}
      {selectedRows.length > 0 && (
        <div className={s.selBar}>
          <Button appearance="primary" onClick={() => setBulkOpen(true)}>
            {`Edit ${selectedRows.length} Selected`}
          </Button>
        </div>
      )}
      {visible.length === 0 ? (
        <Body1 className={s.none}>Nothing matches the current filters.</Body1>
      ) : (
        <div className={s.scroll}>
          <Table size="small" aria-label="Notification recipients" className={mergeClasses(roomy, s.table)}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: 44 }}>
                  <Checkbox
                    checked={allVisibleSelected ? true : selected.size > 0 ? 'mixed' : false}
                    onChange={toggleAllVisible}
                    aria-label={allVisibleSelected ? 'Unselect all notifications' : 'Select all notifications'}
                  />
                </TableHeaderCell>
                <SortableHeaderCell sortDirection={sort.directionFor('notification')} onSort={() => sort.toggle('notification')}>
                  Notification
                </SortableHeaderCell>
                <SortableHeaderCell sortDirection={sort.directionFor('company')} onSort={() => sort.toggle('company')}>
                  Company
                </SortableHeaderCell>
                <SortableHeaderCell sortDirection={sort.directionFor('assigned')} onSort={() => sort.toggle('assigned')}>
                  Assigned To
                </SortableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((row) => (
                <TableRow
                  key={`${row.notification_type}:${row.company_id}`}
                  className={clickable}
                  {...rowActivationProps(
                    () => setOpenId(rowKey(row)),
                    `Edit: ${NOTIFICATION_TYPE_LABEL[row.notification_type]}, ${row.company_name}`,
                  )}
                >
                  {/* The checkbox cell swallows its own click, so ticking a box
                      does not also open the row's editor. */}
                  <TableCell onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(rowKey(row))}
                      onChange={() => toggle(rowKey(row))}
                      aria-label={`Select ${NOTIFICATION_TYPE_LABEL[row.notification_type]}, ${row.company_name}`}
                    />
                  </TableCell>
                  <TableCell>{NOTIFICATION_TYPE_LABEL[row.notification_type]}</TableCell>
                  <TableCell>
                    <span className={mergeClasses(isShortOfRecipients(row) && s.short)}>{row.company_name}</span>
                  </TableCell>
                  <TableCell>
                    <div className={s.people}>
                      {assignedTo(row).map((r) => (
                        <span key={r.id}>{personName(r)}</span>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <RecipientsDialog
        row={openRow}
        onClose={() => setOpenId(null)}
        onChanged={(message) => {
          // An empty message is an Apply that was partly refused: the reason is in
          // the pop-up, which stays open, and the list behind it reloads.
          if (message !== '') toast(message)
          void load()
        }}
      />
      <BulkEditDialog
        open={bulkOpen}
        rows={selectedRows}
        onClose={() => setBulkOpen(false)}
        onDone={(message) => {
          if (message !== '') {
            setBulkOpen(false)
            setSelected(new Set())
            toast(message)
          }
          void load()
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// The People Field pop-ups (owner, 2026-10-09 — D-341)
// ---------------------------------------------------------------------------
//
// Both pop-ups work the way every Assignments pop-up does now: name tags in one
// box per list, typing in the box to add, ✕ to take off, everything held until
// Apply, and one "Are you sure?" only when the Apply removes someone.

/** The record an add writes, from a directory pick. */
function recipientOf(u: DirectoryUser): { email: string; name: string | null; upn: string | null } {
  return { email: u.mail ?? u.upn, name: u.display_name || null, upn: u.upn }
}

const displayName = (u: DirectoryUser) => u.display_name || u.upn

/** "will no longer be emailed …", the removal sentence these pop-ups have always used. */
function noLongerEmailed(names: readonly string[], rows: readonly UploadNotificationRow[]): string {
  return `${sentenceList(names)} will no longer be emailed ${notificationsPhrase(rows)} for ${companyNames(rows)}.`
}

/** The search both pop-ups run, keeping the records it found for the pick. */
function useStaffSearch() {
  const [people, setPeople] = useState<DirectoryUser[]>([])
  const search = (t: string) =>
    searchPeople(t).then<EntityOption[]>((found) => {
      setPeople(found)
      return found.map((u) => ({
        id: u.id,
        primary: u.display_name || u.upn,
        secondary: [u.job_title, u.department].filter(Boolean).join(' · ') || u.upn,
      }))
    })
  return { search, find: (id: string) => people.find((p) => p.id === id) ?? null }
}

// ---------------------------------------------------------------------------
// Edit N Selected: everyone on any ticked row, one Apply
// ---------------------------------------------------------------------------

/** One person across the ticked rows, with every list entry they hold there. */
interface BulkRecipient {
  email: string
  name: string
  upn: string | null
  entries: UploadNotificationRecipient[]
  rowKeys: Set<string>
}

function bulkRecipients(rows: readonly UploadNotificationRow[]): BulkRecipient[] {
  const byEmail = new Map<string, BulkRecipient>()
  for (const row of rows) {
    for (const r of row.lists.flatMap((list) => list.recipients)) {
      const key = r.user_email.toLowerCase()
      const entry = byEmail.get(key) ?? {
        email: r.user_email,
        name: personName(r),
        upn: r.user_upn,
        entries: [],
        rowKeys: new Set<string>(),
      }
      entry.entries.push(r)
      entry.rowKeys.add(rowKey(row))
      byEmail.set(key, entry)
    }
  }
  return [...byEmail.values()].sort((a, b) => a.name.localeCompare(b.name))
}

function BulkEditDialog({
  open,
  rows,
  onClose,
  onDone,
}: {
  open: boolean
  rows: readonly UploadNotificationRow[]
  onClose: () => void
  onDone: (message: string) => void
}) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const staff = useStaffSearch()
  const [dialogSurface, setDialogSurface] = useState<HTMLDivElement | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [kind, setKind] = useState<UploadNotificationKind | null>(null)
  // Staged: people to add, people to take off (by email), partial holders to
  // add to the rest.
  const [adds, setAdds] = useState<DirectoryUser[]>([])
  const [drops, setDrops] = useState<ReadonlySet<string>>(() => new Set())
  const [completes, setCompletes] = useState<ReadonlySet<string>>(() => new Set())
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  // A list is asked for only when a ticked row keeps lists to pick from (New
  // Invoices Upload). A row with one list takes the person onto it whatever is
  // picked here.
  const needsList = rows.some((row) => !isSingleListNotification(row.notification_type))
  const mixed = needsList && rows.some((row) => isSingleListNotification(row.notification_type))
  const people = bulkRecipients(rows)
  const changes = adds.length + drops.size + completes.size
  const adding = adds.length + completes.size > 0

  useEffect(() => {
    if (!open) return
    setDialogError(null)
    setKind(null)
    setAdds([])
    setDrops(new Set())
    setCompletes(new Set())
    setConfirmOpen(false)
  }, [open])

  const keyOfEmail = (email: string) => email.toLowerCase()

  function stageAdd(option: EntityOption) {
    const user = staff.find(option.id)
    if (user === null) return
    setDialogError(null)
    const key = keyOfEmail(recipientOf(user).email)
    if (drops.has(key)) {
      setDrops((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
      return
    }
    if (adds.some((u) => u.id === user.id)) return
    if (people.some((p) => keyOfEmail(p.email) === key)) {
      setDialogError(`${displayName(user)} is already listed. Click a dashed name to add the person to the rest.`)
      return
    }
    setAdds((prev) => [...prev, user])
  }

  function stageRemove(key: string) {
    if (key.startsWith('new:')) {
      setAdds((prev) => prev.filter((u) => `new:${u.id}` !== key))
      return
    }
    setCompletes((prev) => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
    setDrops((prev) => new Set(prev).add(key))
  }

  async function save(confirmed = false) {
    if (adding && needsList && kind === null) {
      setDialogError('Choose a list for the people being added.')
      return
    }
    const dropping = people.filter((p) => drops.has(keyOfEmail(p.email)))
    const completing = people.filter((p) => completes.has(keyOfEmail(p.email)) && !drops.has(keyOfEmail(p.email)))
    if (dropping.length > 0 && !confirmed) {
      const added = [...completing.map((p) => p.name), ...adds.map(displayName)]
      setConfirmText(
        // Each person for the companies they are listed on, not the whole selection.
        `${dropping
          .map((p) => noLongerEmailed([p.name], rows.filter((row) => p.rowKeys.has(rowKey(row)))))
          .join(' ')}${added.length > 0 ? ` ${sentenceList(added)} will be added.` : ''}`,
      )
      setConfirmOpen(true)
      return
    }
    setConfirmOpen(false)
    setBusy(true)
    setDialogError(null)
    const failures: string[] = []
    let removed = 0
    for (const p of dropping) {
      for (const entry of p.entries) {
        try {
          await removeUploadNotificationRecipient(entry)
          removed += 1
        } catch (e) {
          failures.push(`${entry.company_name}: ${errorMessage(e)}`)
        }
      }
    }
    const addedFor = new Map<string, number>()
    const toAdd = [
      ...completing.map((p) => ({ name: p.name, rec: { email: p.email, name: p.name, upn: p.upn }, has: p.rowKeys })),
      ...adds.map((u) => ({ name: displayName(u), rec: recipientOf(u), has: new Set<string>() })),
    ]
    // ONE ROW'S REFUSAL DOES NOT ABANDON THE REST: there is no bulk endpoint.
    for (const { name, rec, has } of toAdd) {
      let n = 0
      for (const row of rows) {
        if (has.has(rowKey(row))) continue
        try {
          await addUploadNotificationRecipient({
            notification_type: row.notification_type,
            company_id: row.company_id,
            kind: isSingleListNotification(row.notification_type) ? null : kind,
            user_email: rec.email,
            user_name: rec.name,
            user_upn: rec.upn,
          })
          n += 1
        } catch (e) {
          failures.push(`${row.company_name}: ${errorMessage(e)}`)
        }
      }
      if (n > 0) addedFor.set(name, n)
    }
    setBusy(false)
    const parts: string[] = []
    // Per person: someone already on two of three ticked rows is added for one.
    const list = needsList && !mixed && kind !== null ? ` to ${UPLOAD_NOTIFICATION_KIND_LABEL[kind]}` : ''
    const byCount = new Map<number, string[]>()
    for (const [name, n] of addedFor) byCount.set(n, [...(byCount.get(n) ?? []), name])
    for (const [n, names] of [...byCount.entries()].sort((a, b) => b[0] - a[0])) {
      parts.push(`${sentenceList(names)} ${names.length === 1 ? 'was' : 'were'} added${list} for ${countOf(n, 'company')}.`)
    }
    if (removed > 0) parts.push(`${countOf(removed, 'listing')} ${removed === 1 ? 'was' : 'were'} removed.`)
    const landed = parts.length > 0 ? parts.join(' ') : NOTHING_CHANGED
    if (failures.length > 0) {
      setAdds([])
      setDrops(new Set())
      setCompletes(new Set())
      setDialogError(refusedMessage(landed, failures))
      onDone('')
      return
    }
    onDone(landed)
  }

  return (
    <AppDialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      {/* Hidden while "Are you sure?" is up (owner, 2026-10-09: "This looks
          awkward. Can we hide Edit Assignments"): the question opens over it in
          its own portal, so only the question shows. */}
      <DialogSurface ref={setDialogSurface} style={confirmOpen ? { visibility: 'hidden' } : undefined}>
        <FrozenWhileClosed open={open}>
          <DialogBody>
            <DialogTitle>Edit Assignments</DialogTitle>
            <DialogContent className={f.beforeActions}>
              <div className={mergeClasses(s.form, f.stack)}>
                <ErrorBanner message={dialogError} />
                {/* No one row's name to label it with: no label (D-348). */}
                <PeopleField
                  ariaLabel={`Staff for ${rows.length} selected notifications`}
                  tags={[
                    ...people
                      .filter((p) => !drops.has(keyOfEmail(p.email)))
                      .map<PersonTag>((p) => {
                        const key = keyOfEmail(p.email)
                        const all = p.rowKeys.size === rows.length
                        const completing = completes.has(key)
                        return {
                          key,
                          name: p.name,
                          detail: p.email,
                          tone: completing ? 'new' : all ? 'held' : 'partial',
                          count: all || completing ? undefined : `${p.rowKeys.size} of ${rows.length}`,
                          removable: true,
                          onComplete:
                            all || completing ? undefined : () => setCompletes((prev) => new Set(prev).add(key)),
                        }
                      }),
                    ...adds.map<PersonTag>((u) => ({
                      key: `new:${u.id}`,
                      name: displayName(u),
                      detail: recipientOf(u).email,
                      tone: 'new',
                      removable: true,
                    })),
                  ]}
                  removing={people
                    .filter((p) => drops.has(keyOfEmail(p.email)))
                    .map((p) => ({ key: keyOfEmail(p.email), name: p.name }))}
                  onRemove={stageRemove}
                  onUndo={(key) =>
                    setDrops((prev) => {
                      const next = new Set(prev)
                      next.delete(key)
                      return next
                    })
                  }
                  search={staff.search}
                  onAdd={stageAdd}
                  mountNode={dialogSurface}
                  disabled={busy}
                />
                <Caption1 className={s.bulkScope}>
                  Click a person's name to add that person to all selected notifications.
                  <br />
                  Click a person's "x" to remove that person from all selected notifications.
                </Caption1>
                {/* Asked only when someone is being added to a New Invoices
                    Upload row, which keeps three lists (D-336). */}
                {needsList && adding && (
                  <Field
                    label="Add To"
                    hint={
                      mixed
                        ? 'The list applies to New Invoices Upload. Invoice Passed Without Any Approvals keeps one list, so the person is added to it.'
                        : undefined
                    }
                  >
                    <Dropdown
                      placeholder="Choose a list"
                      disabled={busy}
                      selectedOptions={kind === null ? [] : [kind]}
                      value={kind === null ? '' : UPLOAD_NOTIFICATION_KIND_LABEL[kind]}
                      onOptionSelect={(_, d) => setKind((d.optionValue as UploadNotificationKind) ?? null)}
                    >
                      {UPLOAD_NOTIFICATION_KINDS.map((k) => (
                        <Option key={k} value={k} text={UPLOAD_NOTIFICATION_KIND_LABEL[k]}>
                          {UPLOAD_NOTIFICATION_KIND_LABEL[k]}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => void save()}
                disabled={busy || changes === 0 || (adding && needsList && kind === null)}
              >
                {busy ? 'Applying…' : 'Apply'}
              </Button>
            </DialogActions>
          </DialogBody>
        </FrozenWhileClosed>
        {/* ARE YOU SURE? — the standard pop-up, nested so it opens over this one. */}
        <DecisionDialog
          open={confirmOpen}
          title="Are you sure?"
          body={confirmText}
          requireReason={false}
          confirmLabel="Apply"
          submitting={busy}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => void save(true)}
        />
      </DialogSurface>
    </AppDialog>
  )
}

// ---------------------------------------------------------------------------
// The row's editor
// ---------------------------------------------------------------------------

function RecipientsDialog({
  row,
  onClose,
  onChanged,
}: {
  row: UploadNotificationRow | null
  onClose: () => void
  onChanged: (message: string) => void
}) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const staff = useStaffSearch()
  const [dialogSurface, setDialogSurface] = useState<HTMLDivElement | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Staged, per list: people to add (keyed by the list's kind, `single` for the
  // one list), and listings to take off (by recipient id).
  const [adds, setAdds] = useState<Record<string, DirectoryUser[]>>({})
  const [drops, setDrops] = useState<ReadonlySet<string>>(() => new Set())
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  useEffect(() => {
    setDialogError(null)
    setAdds({})
    setDrops(new Set())
    setConfirmOpen(false)
  }, [row?.company_id, row?.notification_type])

  const listKey = (kind: UploadNotificationKind | null) => kind ?? 'single'
  const allAdds = Object.values(adds).flat()
  const changes = allAdds.length + drops.size

  function stageAdd(kind: UploadNotificationKind | null, option: EntityOption) {
    if (row === null) return
    const user = staff.find(option.id)
    if (user === null) return
    setDialogError(null)
    const email = recipientOf(user).email
    const listed = listOf(row, kind)?.recipients.find((r) => sameEmail(r.user_email, email))
    if (listed !== undefined) {
      if (drops.has(listed.id)) {
        setDrops((prev) => {
          const next = new Set(prev)
          next.delete(listed.id)
          return next
        })
        return
      }
      setDialogError(`${displayName(user)} is already on this list.`)
      return
    }
    const key = listKey(kind)
    if ((adds[key] ?? []).some((u) => u.id === user.id)) return
    setAdds((prev) => ({ ...prev, [key]: [...(prev[key] ?? []), user] }))
  }

  function stageRemove(kind: UploadNotificationKind | null, key: string) {
    if (key.startsWith('new:')) {
      const k = listKey(kind)
      setAdds((prev) => ({ ...prev, [k]: (prev[k] ?? []).filter((u) => `new:${u.id}` !== key) }))
      return
    }
    setDrops((prev) => new Set(prev).add(key))
  }

  async function save(confirmed = false) {
    if (row === null) return
    const removals = row.lists.flatMap((list) => list.recipients).filter((r) => drops.has(r.id))
    if (removals.length > 0 && !confirmed) {
      const added = allAdds.map(displayName)
      setConfirmText(
        `${noLongerEmailed([...new Set(removals.map(personName))], [row])}${added.length > 0 ? ` ${sentenceList(added)} will be added.` : ''}`,
      )
      setConfirmOpen(true)
      return
    }
    setConfirmOpen(false)
    setBusy(true)
    setDialogError(null)
    const failures: string[] = []
    const removed: string[] = []
    const added: string[] = []
    for (const r of removals) {
      try {
        await removeUploadNotificationRecipient(r)
        removed.push(personName(r))
      } catch (e) {
        failures.push(`${personName(r)}: ${errorMessage(e)}`)
      }
    }
    for (const list of row.lists) {
      for (const user of adds[listKey(list.kind)] ?? []) {
        const rec = recipientOf(user)
        try {
          await addUploadNotificationRecipient({
            notification_type: row.notification_type,
            company_id: row.company_id,
            kind: list.kind,
            user_email: rec.email,
            user_name: rec.name,
            user_upn: rec.upn,
          })
          added.push(list.kind === null ? displayName(user) : `${displayName(user)} (${list.label})`)
        } catch (e) {
          failures.push(`${displayName(user)}: ${errorMessage(e)}`)
        }
      }
    }
    setBusy(false)
    const parts: string[] = []
    const where = rowTitle(row)
    if (added.length > 0) parts.push(`${sentenceList(added)} ${added.length === 1 ? 'was' : 'were'} added to ${where}.`)
    if (removed.length > 0) {
      const names = [...new Set(removed)]
      parts.push(`${sentenceList(names)} ${names.length === 1 ? 'was' : 'were'} removed from ${where}.`)
    }
    const landed = parts.length > 0 ? parts.join(' ') : NOTHING_CHANGED
    if (failures.length > 0) {
      setAdds({})
      setDrops(new Set())
      setDialogError(refusedMessage(landed, failures))
      onChanged('')
      return
    }
    onClose()
    onChanged(landed)
  }

  return (
    <AppDialog open={row !== null} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface ref={setDialogSurface} style={confirmOpen ? { visibility: 'hidden' } : undefined}>
        <FrozenWhileClosed open={row !== null}>
          <DialogBody>
            {/* ONE TITLE FOR EVERY ASSIGNMENTS POP-UP (owner, 2026-10-09); the
                notification and company it opened on, which were the title, follow. */}
            <DialogTitle>Edit Assignments</DialogTitle>
            <DialogContent className={f.beforeActions}>
              <div className={mergeClasses(s.form, f.stack)}>
                <ErrorBanner message={dialogError} />
                {/* The notification and company label the one list's field (owner,
                    2026-10-11 — D-348), where "Staff" was. With three lists,
                    each keeps its own label and this line leads instead. */}
                {row && row.lists.length > 1 && <Body1>{rowLabel(row)}</Body1>}
                {/* New Invoices Upload's three lists, or the one list of
                    Invoice Passed Without Any Approvals, headed Assigned To
                    (D-336) — one People Field each (D-341). */}
                {(row?.lists ?? []).map(({ kind, label, recipients: listed }) => (
                  <PeopleField
                    key={listKey(kind)}
                    label={kind === null && row ? rowLabel(row) : label}
                    tags={[
                      ...listed
                        .filter((r) => !drops.has(r.id))
                        .map<PersonTag>((r) => ({
                          key: r.id,
                          name: personName(r),
                          detail: r.user_email,
                          tone: 'held',
                          removable: true,
                        })),
                      ...(adds[listKey(kind)] ?? []).map<PersonTag>((u) => ({
                        key: `new:${u.id}`,
                        name: displayName(u),
                        detail: recipientOf(u).email,
                        tone: 'new',
                        removable: true,
                      })),
                    ]}
                    removing={listed.filter((r) => drops.has(r.id)).map((r) => ({ key: r.id, name: personName(r) }))}
                    onRemove={(key) => stageRemove(kind, key)}
                    onUndo={(key) =>
                      setDrops((prev) => {
                        const next = new Set(prev)
                        next.delete(key)
                        return next
                      })
                    }
                    search={staff.search}
                    onAdd={(option) => stageAdd(kind, option)}
                    mountNode={dialogSurface}
                    disabled={busy}
                  />
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={() => void save()} disabled={busy || changes === 0}>
                {busy ? 'Applying…' : 'Apply'}
              </Button>
            </DialogActions>
          </DialogBody>
        </FrozenWhileClosed>
        {/* ARE YOU SURE? — the standard pop-up, nested so it opens over this one. */}
        <DecisionDialog
          open={confirmOpen}
          title="Are you sure?"
          body={confirmText}
          requireReason={false}
          confirmLabel="Apply"
          submitting={busy}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => void save(true)}
        />
      </DialogSurface>
    </AppDialog>
  )
}
